"use client"

import type { GachaEvmBuybackQuote } from "@anaconda/core/gacha/evm-buyback"
import { gachaEvmAddressSchema } from "@anaconda/core/gacha/evm-chains"
import { Trans } from "@lingui/react/macro"
import { useConnectWallet, usePrivy } from "@privy-io/react-auth"
import {
  Button,
  Dialog,
  cn,
  sonnerToast,
} from "@rarible_int/anaconda_ui_components"
import { atom, wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useQueryClient } from "@tanstack/react-query"
import { type ReactNode, useEffect, useMemo, useRef } from "react"
import { formatUnits } from "viem"
import type { GachaBuybackConfirmationItem } from "./gacha-buyback-confirmation-body"
import {
  type GachaBuybackOfferStatus,
  getGachaBuybackOfferStatus,
} from "./gacha-buyback-offer-state"
import { deriveBuybackRatePercent } from "./gacha-buyback-rate"
import {
  type GachaEvmBuybackOffer,
  type GachaEvmBuybackWallet,
  createGachaEvmBuyback,
  formatGachaEvmBuyback,
  formatGachaEvmBuybackButtonPrice,
} from "./gacha-evm-buyback"
import { requestGachaEvmBalanceRefresh } from "./gacha-evm-payment-balance"
import { requestGachaPortfolioRefresh } from "./gacha-portfolio-refresh"
import { isGachaWalletCancellationMessage } from "./gacha-wallet-cancellation"
import { GachaBuybackConfirmationDialog } from "./portfolio/gacha-buyback-confirmation-dialog"
import type { GachaEvmCardIdentity } from "./portfolio/gacha-evm-portfolio"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaEvmBuybackPrice } from "./use-gacha-evm-buyback-price"
import { useGachaEvmWallets } from "./use-gacha-evm-wallets"
import { useGachaNetworkContext } from "./use-gacha-network"

function createGachaEvmBuybackButtonState<T>(
  initialize: () => T,
  name: string,
) {
  return atom(initialize(), name)
}

export const GachaEvmBuybackButton = reatomComponent<{
  identity: GachaEvmCardIdentity
  walletAddress: string
  paymentToken?: string
  item: GachaBuybackConfirmationItem
  portfolio?: boolean
  className?: string
  fullWidth?: boolean
  whenIneligible?: "hide" | "placeholder"
  onComplete?: (label: string) => void
  renderResult?: (state: {
    buybackOffer: string
    boughtBackLabel: string
    buybackBusy: boolean
    canAcceptBuyback: boolean
    offerStatus: GachaBuybackOfferStatus
    offerSlow: boolean
    eligibilityUntil: number | undefined
    onRetryOffer?: () => void
    onRequestBuyback: () => void
  }) => ReactNode
}>(
  ({
    identity,
    walletAddress,
    paymentToken,
    item,
    portfolio = false,
    className,
    fullWidth = false,
    whenIneligible = "placeholder",
    onComplete,
    renderResult,
  }) => {
    const preload = portfolio || Boolean(renderResult)
    const { wallets } = useGachaEvmWallets()
    const { authenticated, user } = usePrivy()
    const network = useGachaNetworkContext()
    const apiOptions = useGachaApiOptions()
    const queryClient = useQueryClient()
    const key = `${authenticated}:${user?.id ?? ""}:${network.environment}:${identity.chainId}:${identity.contract.toLowerCase()}:${identity.tokenId}:${walletAddress.toLowerCase()}:${paymentToken ?? ""}`
    const current = useRef({ key, wallets, selected: network.network })
    current.current = { key, wallets, selected: network.network }
    const connection = useRef<{
      resolve: (wallet: GachaEvmBuybackWallet) => void
      reject: (error: Error) => void
    } | null>(null)
    const { connectWallet } = useConnectWallet({
      onSuccess: wrap(({ wallet }) => {
        const pending = connection.current
        if (!pending) return
        connection.current = null
        if (
          current.current.key !== key ||
          wallet.type !== "ethereum" ||
          wallet.address.toLowerCase() !== walletAddress.toLowerCase()
        ) {
          pending.reject(
            new Error(
              "Connect the original buyback wallet to confirm this offer.",
            ),
          )
          return
        }
        pending.resolve(wallet)
      }),
      onError: wrap(() => {
        const pending = connection.current
        connection.current = null
        pending?.reject(
          new Error("Wallet connection cancelled or failed. Try again."),
        )
      }),
    })
    const connectionPrompt = useRef(connectWallet)
    connectionPrompt.current = connectWallet
    const quoteState = useMemo(
      () =>
        createGachaEvmBuybackButtonState<GachaEvmBuybackQuote | null>(
          () => null,
          "gacha-evm-buyback-button.quote",
        ),
      [],
    )
    const quote = quoteState()
    const setQuote = quoteState.set
    const openState = useMemo(
      () =>
        createGachaEvmBuybackButtonState(
          () => false,
          "gacha-evm-buyback-button.open",
        ),
      [],
    )
    const open = openState()
    const setOpen = openState.set
    const busyState = useMemo(
      () =>
        createGachaEvmBuybackButtonState(
          () => false,
          "gacha-evm-buyback-button.busy",
        ),
      [],
    )
    const busy = busyState()
    const setBusy = busyState.set
    const errorState = useMemo(
      () =>
        createGachaEvmBuybackButtonState(
          () => "",
          "gacha-evm-buyback-button.error",
        ),
      [],
    )
    const error = errorState()
    const setError = errorState.set
    const completedState = useMemo(
      () =>
        createGachaEvmBuybackButtonState(
          () => "",
          "gacha-evm-buyback-button.completed",
        ),
      [],
    )
    const completed = completedState()
    const setCompleted = completedState.set
    const settlementPendingState = useMemo(
      () =>
        createGachaEvmBuybackButtonState(
          () => false,
          "gacha-evm-buyback-button.settlementPending",
        ),
      [],
    )
    const settlementPending = settlementPendingState()
    const setSettlementPending = settlementPendingState.set
    const retryingState = useMemo(
      () => atom(false, "gacha-evm-buyback-button.retrying"),
      [],
    )
    const retrying = retryingState()
    const slowState = useMemo(
      () => atom(false, "gacha-evm-buyback-button.slow"),
      [],
    )
    const slow = slowState()
    const active = useRef(true)
    useEffect(
      wrap(() => {
        active.current = true
        return () => {
          active.current = false
          connection.current?.reject(
            new Error("Buyback dialog is no longer active."),
          )
          connection.current = null
        }
      }),
      [],
    )
    const { availability, price, priceOptions, refreshOffer } =
      useGachaEvmBuybackPrice({
        identity,
        walletAddress,
        paymentToken,
        enabled: preload && !completed,
        retryTransient: Boolean(renderResult),
      })
    // biome-ignore lint/correctness/useExhaustiveDependencies: changing the complete identity resets presentation, not an individual state dependency.
    useEffect(
      wrap(() => {
        connection.current?.reject(
          new Error(
            "Original buyback wallet or network changed. Reopen the card.",
          ),
        )
        connection.current = null
        setQuote(null)
        setOpen(false)
        setCompleted("")
        setError("")
        retryingState.set(false)
        slowState.set(false)
      }),
      [key],
    )
    const session = useMemo(() => {
      if (!network.environment) return null
      const assertContext = () => {
        if (
          !active.current ||
          current.current.key !== key ||
          (preload && current.current.selected !== identity.chainId)
        )
          throw new Error(
            "Original buyback wallet or network changed. Reopen the card.",
          )
      }
      return createGachaEvmBuyback(
        {
          playerAddress: gachaEvmAddressSchema.parse(walletAddress),
          evmContract: gachaEvmAddressSchema.parse(identity.contract),
          evmTokenId: identity.tokenId,
          chainId: identity.chainId,
          ...(paymentToken
            ? { paymentToken: gachaEvmAddressSchema.parse(paymentToken) }
            : {}),
        },
        {
          environment: network.environment,
          getWallet: async () => {
            assertContext()
            const wallet = current.current.wallets.find(
              (candidate) =>
                candidate.address.toLowerCase() === walletAddress.toLowerCase(),
            )
            if (
              wallet &&
              ("authorizeConnection" in wallet ||
                (await wrap(wallet.isConnected())))
            )
              return wallet
            assertContext()
            return await wrap(
              new Promise<GachaEvmBuybackWallet>((resolve, reject) => {
                const timeout = window.setTimeout(
                  wrap(() => {
                    connection.current = null
                    reject(new Error("Wallet connection timed out. Try again."))
                  }),
                  60_000,
                )
                connection.current = {
                  resolve: (wallet) => {
                    window.clearTimeout(timeout)
                    resolve(wallet)
                  },
                  reject: (error) => {
                    window.clearTimeout(timeout)
                    reject(error)
                  },
                }
                try {
                  connectionPrompt.current({ walletChainType: "ethereum-only" })
                } catch (error) {
                  window.clearTimeout(timeout)
                  connection.current = null
                  reject(error)
                }
              }),
            )
          },
          apiOptions,
          assertContext,
          refresh: () => {
            requestGachaEvmBalanceRefresh()
            requestGachaPortfolioRefresh()
            void wrap(
              queryClient.invalidateQueries({
                queryKey: ["gacha-evm-buyback-price"],
                refetchType: "none",
              }),
            )
            void wrap(
              queryClient.invalidateQueries({
                queryKey: ["gacha-evm-buyback-available"],
              }),
            )
            void wrap(
              queryClient.invalidateQueries({
                queryKey: ["gacha-portfolio-activity"],
              }),
            )
          },
        },
      )
      // Session survives SDK array recreation; assertContext checks live identity.
    }, [
      key,
      apiOptions,
      queryClient,
      walletAddress,
      identity.chainId,
      identity.contract,
      identity.tokenId,
      network.environment,
      paymentToken,
      preload,
    ])
    const prepare = async () => {
      setError("")
      try {
        if (!session)
          throw new Error("Buyback network configuration is unavailable.")
        const cached =
          preload && !session.hasBroadcast()
            ? queryClient.getQueryData<GachaEvmBuybackOffer>(
                priceOptions.queryKey,
              )
            : undefined
        if (
          cached &&
          cached.quote.deadline * 1000 > Date.now() &&
          cached.eligibilityUntil > Date.now()
        ) {
          const result = session.review(cached)
          setQuote(result.quote)
          setOpen(true)
          return
        }
        setBusy(true)
        setOpen(true)
        const offer =
          preload && !session.hasBroadcast()
            ? await wrap(queryClient.fetchQuery(priceOptions))
            : undefined
        const result = offer
          ? session.review(offer)
          : await wrap(session.prepare())
        if (!active.current || current.current.key !== key) return
        setQuote(result.quote)
      } catch (error) {
        setOpen(true)
        setError(
          error instanceof Error ? error.message : "Unable to check buyback.",
        )
      } finally {
        setBusy(false)
      }
    }
    const sell = async () => {
      if (!session || busy) return
      setBusy(true)
      setError("")
      try {
        if (
          !session.hasBroadcast() &&
          (!quote || quote.deadline * 1000 <= Date.now())
        ) {
          const offer = preload
            ? await wrap(queryClient.fetchQuery(priceOptions))
            : undefined
          const renewed = offer
            ? session.review(offer)
            : await wrap(session.prepare())
          setQuote(renewed.quote)
          setError("Quote renewed. Review the payout and confirm again.")
          return
        }
        const result = await wrap(session.sell())
        const label = quote ? formatGachaEvmBuyback(quote, "usd") : result.label
        setCompleted(label)
        setSettlementPending(result.settlementPending)
        setOpen(false)
        onComplete?.(label)
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Buyback failed; no payout confirmed."
        if (isGachaWalletCancellationMessage(message)) {
          sonnerToast.error(message)
        } else {
          setError(message)
        }
      } finally {
        setBusy(false)
      }
    }
    const livePrice =
      preload &&
      availability.eligible === true &&
      !price.isError &&
      !price.isPlaceholderData &&
      price.data &&
      price.data.quote.deadline * 1000 > Date.now() &&
      price.data.eligibilityUntil > Date.now()
        ? formatGachaEvmBuybackButtonPrice(price.data.quote, "usd")
        : undefined
    // Pricing never acquires a signer. Only explicit confirmation can sell.
    const ineligible =
      preload && availability.eligible === false && !session?.hasBroadcast()
    const waitingForPrice =
      preload &&
      !open &&
      !livePrice &&
      !session?.hasBroadcast() &&
      (availability.query.isLoading || price.isFetching)
    const offerStatus = getGachaBuybackOfferStatus({
      eligible: availability.eligible,
      hasPrice: Boolean(livePrice),
      loading: retrying || availability.query.isLoading || price.isFetching,
      failed: availability.query.isError || price.isError,
      availabilitySettled: availability.query.isSuccess,
    })
    const reveal = Boolean(renderResult)
    // biome-ignore lint/correctness/useExhaustiveDependencies: wrap captures the callback; each card/loading phase owns its delay timer.
    useEffect(
      wrap(() => {
        slowState.set(false)
        if (!reveal || offerStatus !== "loading") return
        const timer = window.setTimeout(
          wrap(() => slowState.set(true)),
          8000,
        )
        return () => window.clearTimeout(timer)
      }),
      [key, offerStatus, reveal, slowState],
    )
    const retryOffer = async () => {
      if (
        retrying ||
        busy ||
        completed ||
        !network.environment ||
        network.network !== identity.chainId
      )
        return
      retryingState.set(true)
      try {
        await wrap(refreshOffer())
      } catch {
        // Query state supplies the retry message without opening a sale dialog.
      } finally {
        if (active.current && current.current.key === key)
          retryingState.set(false)
      }
    }
    return (
      <>
        {renderResult ? (
          renderResult({
            buybackOffer:
              completed && quote
                ? formatGachaEvmBuybackButtonPrice(quote, "usd")
                : (livePrice ?? "—"),
            boughtBackLabel: completed,
            buybackBusy: busy,
            canAcceptBuyback:
              Boolean(network.environment) &&
              network.network === identity.chainId &&
              !completed &&
              offerStatus === "ready",
            offerStatus,
            offerSlow: slow,
            eligibilityUntil:
              availability.eligible === true
                ? availability.eligibilityUntil
                : undefined,
            onRetryOffer:
              network.environment && network.network === identity.chainId
                ? wrap(() => {
                    void wrap(retryOffer())
                  })
                : undefined,
            onRequestBuyback: wrap(() => {
              if (offerStatus === "ready") void wrap(prepare())
            }),
          })
        ) : completed ? (
          <output>
            <Trans>Buyback complete</Trans> · {completed}{" "}
            <Trans>payout confirmed</Trans>
            {settlementPending && (
              <span>
                {" "}
                · <Trans>CollectorCrypt status sync pending</Trans>
              </span>
            )}
          </output>
        ) : ineligible || waitingForPrice ? (
          whenIneligible === "hide" ? null : (
            <span className="text-foreground/50">—</span>
          )
        ) : portfolio && offerStatus !== "ready" ? (
          <Button
            type="button"
            className={cn(
              "h-auto min-h-9 whitespace-nowrap px-3 py-2.5 text-sm leading-snug",
              fullWidth && "w-full",
              className,
            )}
            data-testid="gacha-evm-portfolio-buyback-button"
            disabled={busy || retrying}
            onClick={wrap(() => {
              void wrap(retryOffer())
            })}
          >
            <Trans>Retry offer</Trans>
          </Button>
        ) : (
          <Button
            type="button"
            className={cn(
              portfolio &&
                "h-auto min-h-9 whitespace-nowrap px-3 py-2.5 text-sm leading-snug",
              fullWidth && "w-full",
              className,
            )}
            data-testid={
              portfolio ? "gacha-evm-portfolio-buyback-button" : undefined
            }
            disabled={busy}
            onClick={wrap(() => {
              void wrap(prepare())
            })}
          >
            {portfolio ? (
              <Trans>Accept Buyback</Trans>
            ) : (
              <Trans>Check Buyback</Trans>
            )}
            {livePrice && <> {livePrice}</>}
          </Button>
        )}
        <Dialog
          open={open}
          onOpenChange={(value) => {
            if (!busy) setOpen(value)
          }}
        >
          <GachaBuybackConfirmationDialog
            className={renderResult ? "z-[230]" : undefined}
            overlayClassName={renderResult ? "z-[220]" : undefined}
            item={{
              ...item,
              buybackOffer: quote ? formatGachaEvmBuyback(quote, "usd") : "—",
              buybackRatePercent: quote
                ? deriveBuybackRatePercent(
                    item.marketValue,
                    formatUnits(
                      BigInt(quote.refundAmountBase),
                      quote.token.decimals,
                    ),
                  )
                : null,
            }}
            payoutLoading={!quote && busy}
            confirmDisabled={!quote}
            buybackBusy={busy}
            onCancel={() => setOpen(false)}
            onConfirm={wrap(() => {
              void wrap(sell())
            })}
          >
            {error && <p role="alert">{error}</p>}
            {!quote && !busy && (
              <Button
                onClick={wrap(() => {
                  void wrap(prepare())
                })}
              >
                <Trans>Retry</Trans>
              </Button>
            )}
          </GachaBuybackConfirmationDialog>
        </Dialog>
      </>
    )
  },
  "GachaEvmBuybackButton",
)
